import React, { useState } from 'react';
import { Plus, FileText, Users, Calendar, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '../../stores/uiStore';
import { toast } from 'react-hot-toast';

export function QuickActions() {
  const navigate = useNavigate();
  const { openModal } = useUIStore();
  const [hoveredAction, setHoveredAction] = useState<string | null>(null);

  const actions = [
    {
      id: 'new-proposal',
      name: 'New Proposal',
      description: 'Create a new proposal',
      icon: Plus,
      onClick: () => {
        openModal('newProposal');
      },
      color: 'bg-purple-600',
      hoverColor: 'hover:bg-purple-700',
      textColor: 'text-purple-600',
      bgLight: 'bg-purple-50',
    },
    {
      id: 'view-contracts',
      name: 'View Contracts',
      description: 'Browse active contracts',
      icon: FileText,
      onClick: () => navigate('/contracts'),
      color: 'bg-blue-600',
      hoverColor: 'hover:bg-blue-700',
      textColor: 'text-blue-600',
      bgLight: 'bg-blue-50',
    },
    {
      id: 'team-management',
      name: 'Team Management',
      description: 'Manage team members',
      icon: Users,
      onClick: () => navigate('/marketplace/collaboration?tab=team'),
      color: 'bg-green-600',
      hoverColor: 'hover:bg-green-700',
      textColor: 'text-green-600',
      bgLight: 'bg-green-50',
    },
    {
      id: 'schedule-meeting',
      name: 'Schedule Meeting',
      description: 'Set up a new meeting',
      icon: Calendar,
      onClick: () => openModal('scheduleMeeting'),
      color: 'bg-amber-600',
      hoverColor: 'hover:bg-amber-700',
      textColor: 'text-amber-600',
      bgLight: 'bg-amber-50',
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {actions.map((action) => {
        const Icon = action.icon;
        const isHovered = hoveredAction === action.id;

        return (
          <button
            key={action.id}
            onClick={action.onClick}
            onMouseEnter={() => setHoveredAction(action.id)}
            onMouseLeave={() => setHoveredAction(null)}
            className="group relative overflow-hidden rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            <div className="relative z-10 flex items-center">
              <div
                className={`rounded-xl ${action.color} p-3 transition-transform duration-300 ${
                  isHovered ? 'scale-110' : ''
                }`}
              >
                <Icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className={`font-medium text-gray-900 transition-colors ${
                  isHovered ? action.textColor : ''
                }`}>
                  {action.name}
                </h3>
                <p className="mt-1 text-sm text-gray-500">{action.description}</p>
              </div>
              <ArrowRight
                className={`h-5 w-5 transform text-gray-400 transition-all duration-300 ${
                  isHovered ? `${action.textColor} translate-x-1` : ''
                }`}
              />
            </div>

            <div
              className={`absolute inset-0 -z-10 opacity-0 transition-opacity duration-300 ${
                isHovered ? 'opacity-10' : ''
              }`}
              style={{
                background: `radial-gradient(circle at center, ${action.color} 0%, transparent 70%)`,
              }}
            />

            <div
              className={`absolute bottom-0 left-0 h-1 w-full transform bg-gradient-to-r transition-all duration-500 ${
                isHovered ? 'scale-100' : 'scale-x-0'
              }`}
              style={{
                background: `linear-gradient(to right, transparent, ${action.color}, transparent)`,
              }}
            />

            <div
              className={`absolute inset-0 -z-20 transition-opacity duration-300 ${
                isHovered ? 'opacity-5' : 'opacity-0'
              } ${action.bgLight}`}
            />
          </button>
        );
      })}
    </div>
  );
}